import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as api from '../api/api';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../config/rbac';
import { PAGE_SIZE, formatDate } from '../lib/format';
import { dateInputToEpochRange } from '../lib/dateRange';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, ErrorBanner } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';

const EMPTY_FILTERS = {
  search: '',
  firstScanOnly: '',
  fromDate: '',
  toDate: '',
};

export default function Verifications() {
  const { admin, hasRole } = useAuth();
  const isSuper = hasRole(ROLES.SUPER_ADMIN);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [skip, setSkip] = useState(0);
  const [limit] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [firstScanOnly, setFirstScanOnly] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [brandFilter, setBrandFilter] = useState(() => searchParams.get('brandId') || '');
  const [brands, setBrands] = useState([]);
  const loadSeqRef = useRef(0);

  const brandIdFromUrl = searchParams.get('brandId') || '';
  const filteredBrandName = brands.find((b) => b.id === brandIdFromUrl)?.name;

  useEffect(() => {
    if (!isSuper) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.listBrands({ limit: 200 });
        if (!cancelled) setBrands(data?.items || []);
      } catch {
        /* filter dropdown is optional */
      }
    })();
    return () => { cancelled = true; };
  }, [isSuper]);

  useEffect(() => {
    setBrandFilter(brandIdFromUrl);
    setSkip(0);
  }, [brandIdFromUrl]);

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const params = { skip, limit };
      const q = filters.search.trim();
      if (q) params.search = q;
      if (filters.firstScanOnly === 'yes') params.isFirstScan = true;
      if (filters.firstScanOnly === 'no') params.isFirstScan = false;
      if (isSuper && brandIdFromUrl) params.brandId = brandIdFromUrl;

      const range = dateInputToEpochRange(filters.fromDate, filters.toDate);
      if (range.scannedAtFrom != null) params.scannedAtFrom = range.scannedAtFrom;
      if (range.scannedAtTo != null) params.scannedAtTo = range.scannedAtTo;

      const raw = await api.listVerifications(params);
      if (seq !== loadSeqRef.current) return;

      const norm = normalizeListResponse(raw, limit);
      setRows(norm.items);
      setTotalCount(norm.totalCount);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      setError(pickErrorMessage(err));
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [skip, limit, filters, isSuper, brandIdFromUrl]);

  useEffect(() => { load(); }, [load]);

  function applyFilters() {
    if (fromDate && toDate && fromDate > toDate) {
      setError('From date must be on or before to date.');
      return;
    }
    setError('');
    setSkip(0);
    setFilters({
      search,
      firstScanOnly,
      fromDate,
      toDate,
    });
    if (!isSuper) return;
    const next = new URLSearchParams(searchParams);
    if (brandFilter) next.set('brandId', brandFilter);
    else next.delete('brandId');
    setSearchParams(next, { replace: true });
  }

  const columns = [
    { key: 'code', label: 'Coupon code' },
    ...(isSuper ? [{ key: 'brandName', label: 'Brand' }] : []),
    {
      key: 'isFirstScan',
      label: 'Type',
      render: (r) => (
        <span className={`badge ${r.isFirstScan ? 'text-bg-success' : 'text-bg-secondary'}`}>
          {r.isFirstScan ? 'First scan' : 'Repeat'}
        </span>
      ),
    },
    { key: 'deviceType', label: 'Device', render: (r) => r.deviceType || '—' },
    { key: 'scannedAt', label: 'When', render: (r) => formatDate(r.scannedAt) },
  ];

  return (
    <>
      <PageHeader
        title="Verifications"
        subtitle={
          filteredBrandName
            ? `Scan activity for ${filteredBrandName}`
            : isSuper
              ? 'Platform-wide coupon scan activity'
              : `Scan history for ${admin?.brandName || 'your brand'}`
        }
      />
      <ErrorBanner message={error} />
      <Card className="mb-3 shadow-sm">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small mb-1">Search code</label>
              <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Coupon code…" />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">Scan type</label>
              <select className="form-select" value={firstScanOnly} onChange={(e) => setFirstScanOnly(e.target.value)}>
                <option value="">All scans</option>
                <option value="yes">First scan only</option>
                <option value="no">Repeat scans</option>
              </select>
            </div>
            {isSuper ? (
              <div className="col-md-3">
                <label className="form-label small mb-1">Brand</label>
                <select className="form-select" value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
                  <option value="">All brands</option>
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <div className="row g-2 align-items-end mt-2">
            <div className="col-md-3">
              <label className="form-label small mb-1">From date</label>
              <input
                type="date"
                className="form-control"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small mb-1">To date</label>
              <input
                type="date"
                className="form-control"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <FilterSubmitButton onClick={applyFilters} disabled={loading} />
            </div>
          </div>
        </div>
      </Card>
      <Card className="shadow-sm">
        {loading ? (
          <div className="card-body text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              emptyTitle="No verifications yet"
              emptyDescription={
                brandIdFromUrl
                  ? filteredBrandName
                    ? `No scan activity found for ${filteredBrandName}.`
                    : 'No scan activity found for this brand.'
                  : 'Scans appear here when consumers verify coupon codes.'
              }
              rowKey={(r) => r.id}
            />
            <Pagination totalCount={totalCount} skip={skip} limit={limit} onChange={({ skip: s }) => setSkip(s)} />
          </>
        )}
      </Card>
    </>
  );
}
